import { useSelector } from 'react-redux';
import { getCurrentBoard } from '../../../reducers/currentBoardId/currentBoardIdSelector';
import { getBackgroundColorCssValue } from '../../../canvas/background/boardBackgroundUtils';
import { ToolbarListType } from '../../../workspace/toolbar/toolbarTypes';
import React, { useEffect, useState } from 'react';
import chroma from 'chroma-js';
import { getThemeIsDarkMode } from '../../../user/account/accountModal/interface/themeSettings/themeSelector';
import { trim } from 'lodash/fp';

enum ToolbarBackgroundType {
    DEFAULT = 'default',
    CANVAS = 'canvas',
}

const getToolbarBackgroundForType = (type: ToolbarListType): ToolbarBackgroundType => {
    switch (type) {
        case ToolbarListType.CREATE_ELEMENT:
        case ToolbarListType.SELECTED:
        case ToolbarListType.EDITING:
        case ToolbarListType.EDITING_RANGE:
            return ToolbarBackgroundType.CANVAS;
        default:
            return ToolbarBackgroundType.DEFAULT;
    }
};

const getResolvedToolbarColors = (): {
    default: chroma.Color;
    canvas: chroma.Color;
} => {
    const resolvedCanvasColor = trim(getComputedStyle(document.body).getPropertyValue('--ws-canvas-background'));
    const resolvedUIBackgroundColor = trim(getComputedStyle(document.body).getPropertyValue('--ui-background-primary'));

    return {
        default: chroma(resolvedUIBackgroundColor),
        canvas: chroma(resolvedCanvasColor),
    };
};

/**
 * Returns the resolved toolbar colors based on the current theme and board background,
 * resolved from the current computed CSS values for canvas and UI background.
 */
const useResolvedToolbarColors = (): {
    default: chroma.Color;
    canvas: chroma.Color;
} => {
    const [toolbarChromaColors, setToolbarChromaColors] = useState(getResolvedToolbarColors());

    const currentBoard = useSelector(getCurrentBoard);
    const currentBoardBackgroundColor = getBackgroundColorCssValue(currentBoard);

    const isDarkMode = useSelector(getThemeIsDarkMode);

    useEffect(() => {
        // wait for the next tick to ensure the CSS variables have been updated
        setTimeout(() => {
            const resolvedColors = getResolvedToolbarColors();
            setToolbarChromaColors(resolvedColors);
        }, 1);
    }, [isDarkMode, currentBoardBackgroundColor]); // these aren't used in the effect, but we want to retrigger when they change

    return toolbarChromaColors;
};

const useMobileToolbarColor = (
    toolbarRef: React.RefObject<HTMLDivElement>,
    currentToolListType: ToolbarListType,
    previousToolListType: ToolbarListType,
    transitionProgress: number,
) => {
    const resolvedColors = useResolvedToolbarColors();

    useEffect(() => {
        const backgroundType = getToolbarBackgroundForType(currentToolListType);
        const previousBackgroundType = getToolbarBackgroundForType(previousToolListType);

        const currentToolbarColor = resolvedColors[backgroundType];
        const previousToolbarColor = resolvedColors[previousBackgroundType];

        // no need to blend if the colors are the same
        if (currentToolbarColor === previousToolbarColor) {
            toolbarRef.current?.style.setProperty('--toolbar-color', currentToolbarColor.css());
        }

        // blend the colors based on the transition progress
        const blendedColor = chroma.mix(previousToolbarColor, currentToolbarColor, transitionProgress);

        toolbarRef.current?.style.setProperty('--toolbar-color', blendedColor.css());
    }, [transitionProgress]);
};

export default useMobileToolbarColor;
